/**
 * Keeps <title>, the meta description and the canonical link in step with the client-side route,
 * so they match what the Worker renders for the same URL (crawlers read the server copy; people
 * sharing a tab title read this one).
 */
import { MOUNT } from "./route";
const SITE = "Z S MERCH";
const DEFAULT_DESC = "Z S MERCH — tools, home & garden, electronics and everyday essentials at honest prices, delivered nationwide in 2–7 working days.";

/**
 * "<name> | Z S MERCH", ≤ 60 characters, the name cut on a word — the same format the Worker's
 * server copy uses (`pageTitle` in worker/src/shop-seo.ts), so the tab title matches the crawler's.
 */
function pageTitle(name: string): string {
  const suffix = ` | ${SITE}`;
  const max = 60 - suffix.length;
  let t = name.replace(/\s+/g, " ").trim();
  if (t.length > max) {
    const cut = t.slice(0, max);
    const sp = cut.lastIndexOf(" ");
    t = (sp > max * 0.5 ? cut.slice(0, sp) : cut).replace(/[\s,.;:&/-]+$/, "");
  }
  return `${t}${suffix}`;
}

function setMeta(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

/**
 * The home page's title + description AS THE WORKER RENDERED THEM (`HOME_TITLE` / `homeDescription` in
 * worker/src/shop-seo.ts), snapshotted before any client route touches <head>. Without this, Home's
 * `setSeo(null, null)` replaced the server copy with "Z S MERCH" + DEFAULT_DESC — and Google renders JS.
 * Only taken when the tab LOADED on the home page; a tab that landed elsewhere falls back to the defaults.
 */
const HOME_SNAPSHOT: { title: string; description: string } | null = (() => {
  if (shopPath() !== "/") return null;
  const d = document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.content ?? "";
  return document.title && d ? { title: document.title, description: d } : null;
})();

/** Home: the server's own title/description when we have them, else the site defaults. */
export function setHomeSeo(): void {
  if (!HOME_SNAPSHOT) return setSeo(null, null);
  setSeo(null, HOME_SNAPSHOT.description);
  document.title = HOME_SNAPSHOT.title;
}

export function setSeo(title: string | null, description?: string | null, canonicalQuery = ""): void {
  document.title = title ? pageTitle(title) : SITE;
  setMeta("description", description || DEFAULT_DESC);
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = `${CANONICAL_BASE}${shopPath()}${canonicalQuery}`;
}

/**
 * ⚠️ The canonical is the SERVING origin + mount — exactly what the Worker writes
 * (`servingBase` in worker/src/shop-seo.ts): a page is canonical to itself, wherever it is served.
 * Indexing is decided by the host instead (noindex everywhere but a dedicated shop host), so the
 * test copy at app.nundi.co.za/shop never claims to be — or competes with — the live store.
 * `MOUNT` is the same hostname rule the server's `servingMount` uses, so this is byte-for-byte
 * the base of the server-rendered canonical (verify:shop-nav holds the server half).
 */
const CANONICAL_BASE = `${window.location.origin}${MOUNT}`;

/** The path inside the shop, without the `/shop` mount this tab may be served under. */
function shopPath(): string {
  const p = window.location.pathname;
  const inside = MOUNT && (p === MOUNT || p.startsWith(`${MOUNT}/`)) ? p.slice(MOUNT.length) : p;
  return inside === "" ? "/" : inside;
}
